import classNames from 'classnames'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { IconFCType } from '@ui/icons/types'

import { useActiveTab } from '@hooks/useActiveTab'

import { TabItem, TabSize } from './TabItem/TabItem'

import styles from './Tabs.module.scss'

export type Tab<T = string> = {
  iconLeft?: IconFCType
  iconRight?: IconFCType
  alert?: boolean
  label: string
  tourTarget?: string
  disabled?: boolean
} & ({ id: T; path?: T } | { label: string; path: T; id?: T })

export interface TabsProps<T = string> {
  tabs: Tab<T>[]
  onTabClick?(tab: Tab<T>): void
  size?: TabSize
  initialIndex?: number
  noActive?: boolean
  center?: boolean
  replace?: boolean
}

type Indicator = { x: number; w: number }
type Dir = 'ltr' | 'rtl'

export function Tabs<T = string>(props: TabsProps<T>) {
  const {
    tabs,
    onTabClick,
    initialIndex = 0,
    size = 'md',
    noActive = false,
    center = false,
    replace,
  } = props

  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const innerRef = useRef<HTMLDivElement | null>(null)
  const tabRefs = useRef<Array<HTMLElement | null>>([])

  const { activeIndex: urlIndex } = useActiveTab(tabs, {
    includeNested: true,
    initialIndex,
  })

  const [activeIndex, setActiveIndex] = useState(urlIndex)
  const [prevUrlIndex, setPrevUrlIndex] = useState(urlIndex)

  if (urlIndex !== prevUrlIndex) {
    setPrevUrlIndex(urlIndex)
    setActiveIndex(urlIndex)
  }

  const [indicator, setIndicator] = useState<Indicator>({ x: 0, w: 0 })
  const indicatorRef = useRef(indicator)

  const getDir = (node: HTMLElement | null): Dir => {
    if (!node) return 'ltr'
    const dir = getComputedStyle(node).direction
    return dir === 'rtl' ? 'rtl' : 'ltr'
  }

  const measureActive = useCallback(() => {
    if (noActive) {
      const prev = indicatorRef.current
      if (prev.x !== 0 || prev.w !== 0) setIndicator({ x: 0, w: 0 })
      return
    }

    const el = tabRefs.current[activeIndex]
    const container = innerRef.current
    if (!el || !container) return

    const dir = getDir(container)

    const elRect = el.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    const inlineStartOffset =
      dir === 'rtl' ? containerRect.right - elRect.right : elRect.left - containerRect.left

    const x = dir === 'rtl' ? -inlineStartOffset : inlineStartOffset
    const w = elRect.width

    const prev = indicatorRef.current
    if (Math.abs(prev.x - x) > 0.1 || Math.abs(prev.w - w) > 0.1) {
      const next = { x, w }
      setIndicator(next)
      indicatorRef.current = next
    }
  }, [activeIndex, noActive])

  useLayoutEffect(() => {
    measureActive()
  }, [activeIndex, noActive, tabs, measureActive])

  useEffect(() => {
    const onResize = () => measureActive()
    window.addEventListener('resize', onResize)

    const ro = new ResizeObserver(() => measureActive())
    if (innerRef.current) ro.observe(innerRef.current)
    tabRefs.current.forEach((node) => node && ro.observe(node))

    return () => {
      window.removeEventListener('resize', onResize)
      ro.disconnect()
    }
  }, [measureActive, tabs])

  useEffect(() => {
    if (noActive) return
    const el = tabRefs.current[activeIndex]
    const scroller = scrollerRef.current
    if (!el || !scroller) return
    if (scroller.scrollWidth <= scroller.clientWidth) return

    el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }, [activeIndex, noActive])

  const handleClick = (idx: number, tab: Tab<T>) => {
    if (tab.disabled) return
    onTabClick?.(tab)
    setActiveIndex(idx)
  }

  return (
    <div
      className={classNames(styles.tabsScroller, {
        [styles.center]: center,
      })}
      ref={scrollerRef}
      aria-label='Page sections'
    >
      <div className={styles.tabsInner} ref={innerRef}>
        <div
          className={styles.indicator}
          style={{
            transform: `translateX(${indicator.x}px)`,
            width: noActive ? 0 : indicator.w,
          }}
          aria-hidden
        />
        {tabs.map((tab, idx) => (
          <TabItem
            size={size}
            key={typeof tab.path === 'string' ? tab.path : (tab.path as any)?.pathname ?? idx}
            ref={(el) => {
              tabRefs.current[idx] = el
            }}
            alert={tab.alert ?? false}
            tab={tab}
            isActive={!noActive && idx === activeIndex}
            onClick={() => handleClick(idx, tab)}
            replace={replace}
          />
        ))}
      </div>
    </div>
  )
}
